import {GeoJSON} from 'ol/format';

const roundFloat = (value, decimalPlaces) => {
  return Number(Math.round(parseFloat(value + 'e' + decimalPlaces)) + 'e-' + decimalPlaces);
};

const humanize = (str) => {
  let frags = str.split('_');
  for (let i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
};

const themeUrlMapping = {
  'A1': 'eso-niveau',
  'A2': 'eso-qualite',
  'B1': {
    'debit': 'esu-debit',
    'crues': 'esu-crues',
  },
  'B2': 'esu-qualite',
};

class Theme {
  constructor(options) {
    this.ref = options.ref;
    this.layers = options.layers;
    this.featureRenderer = options.featureRenderer;
    this.groupRenderer = options.groupRenderer;
  }

  generateSearchUrl(theme, codeType, codeValues) {
    const searchUrl = location.href.replace('macommune', 'search');
    return `${searchUrl}?theme=${theme}&${codeType}=${codeValues}&submit=true#search_results" target="_blank`;
  }

  features(data) {
    if (data.hasOwnProperty('details')) {
      const format = new GeoJSON();
      return format.readFeatures(data.details);
    }
    return [];
  }

  communes(data) {
    if (data.hasOwnProperty('synthesis')) {
      const format = new GeoJSON();
      return format.readFeatures(data.synthesis);
    }
    return [];
  }

  headerRenderer(data) {
    let title = '';
    if (data.hasOwnProperty('title') && data.title.length > 0) {
      title = data.title;
    } else if (data.hasOwnProperty('details') && data.details.features.length > 0) {
      let codes = [];
      data.details.features.forEach((feature) => {
        const codeValue = feature.properties.codebss
          ? feature.properties.codebss.split(' / ')[0]
          : feature.properties.codehydro;
        codes.push(codeValue);
      });
      // Thème B1: use debit as default
      const theme = this.ref === 'B1' ? themeUrlMapping[this.ref]['debit'] : themeUrlMapping[this.ref];
      title = `<a href="${this.generateSearchUrl(theme, 'codesbss', codes)}">
                ${data.entities} entité${data.entities > 1 ? 's' : ''} sur ${data.communes} commune${
        data.communes > 1 ? 's' : ''
      }
              </a>`;
    } else if (data.hasOwnProperty('synthesis') && data.synthesis.features.length > 0) {
      title = 'Synthèse';
    } else {
      title = 'Pas de donnée pour la sélection';
    }
    return `<h5 id="data-header" class="data-header">${title}</h5>`;
  }

  detailsRenderer(data) {
    let cards = [];
    for (let f of this.features(data)) {
      const rendered = this.featureRenderer({
        '_id': f.getId(),
        ...f.getProperties(),
      });
      if (rendered) {
        cards.push(rendered);
      }
    }
    return `<div id="details" class="details">
    ${cards.join('\n')}
    </div>`;
  }

  synthesisRenderer(data) {
    let cards = [];
    for (let f of this.communes(data)) {
      cards.push(
        this.groupRenderer({
          '_id': f.getId(),
          ...f.getProperties(),
        })
      );
    }
    return `<div id="synthesis" class="synthesis">
    ${cards.join('\n')}
    </div>`;
  }

  dataRenderer(data) {
    return `${this.headerRenderer(data)}
    ${this.synthesisRenderer(data)}
    ${this.detailsRenderer(data)}`;
  }
}

const themesDefinitions = [
  new Theme({
    ref: 'A1',
    name: 'Suivi des niveaux de nappe',
    layers: ['directions_ecoulement_me_2009', 'limite_nappe_trans_2009_2016', 'piezometres'],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card" id="${record._id}">
      <a target="_blank" href="${record.link}">${record.codebss}</a> ${record.nom || ''} à ${record.commune}
      ${
        record.alti_measures
          ? `<br/><br/>
      Niveau : ${record.alti_measures} mesures du ${record.alti_begin} au ${record.alti_end}<br/>
      Min : ${record.alti_min_valeur} m le ${record.alti_min_date}<br/>
      Max : ${record.alti_max_valeur} m le ${record.alti_max_date}<br/>
      Moy : ${record.alti_moy} m`
          : ''
      }
      ${
        record.temp_measures
          ? `<br/><br/>
      Température : ${record.temp_measures} mesures du ${record.temp_begin} au ${record.temp_end}<br/>
      Min : ${record.temp_min_valeur} ° le ${record.temp_min_date}<br/>
      Max : ${record.temp_max_valeur} ° le ${record.temp_max_date}<br/>
      Moy : ${record.temp_moy} °`
          : ''
      }
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codecommunes', record._id)}">${
        record.commune
      }</a> :
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codesbss', record.codesbss.join())}">${
        record.entities
      } points de mesure</a>
      ${
        record.alti_measures
          ? `<br/>
      Niveau : ${record.alti_measures} mesures du ${record.alti_begin} au ${record.alti_end}`
          : ''
      }
      ${
        record.temp_measures
          ? `<br/>
      Température : ${record.temp_measures} mesures du ${record.temp_begin} au ${record.temp_end}`
          : ''
      }
      </div>`;
    },
  }),
  new Theme({
    ref: 'A2',
    name: 'Suivi de la qualité de la nappe',
    layers: ['limite_nappe_trans_2009_2016', 'qualitometres_eso'],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card" id="${record._id}">
      <a target="_blank" href="${record.link}">${record.codebss}</a> ${record.nom || ''} à ${
        record.commune
      }<br/>
      ${record.analyses} analyses ${
        record.analyses ? `sur ${record.parametres} parametres du ${record.begin} au ${record.end}` : ''
      }
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codecommunes', record._id)}">${
        record.commune
      }</a> : 
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codesbss', record.codesbss.join())}">${
        record.qualitometres
      } points</a>
      avec ${record.analyses} analyses
      sur ${record.parametres} paramètres du ${record.begin} au ${record.end}
      </div>`;
    },
  }),
  new Theme({
    ref: 'A3',
    name: 'ESOU – Informations générales aquifère',
    layers: ['epaisseur_mouillee', 'limite_nappe_trans_2009_2016'],
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
        Epaisseur ${record['seuil']} m : <br/>
        Superficie : <b>${roundFloat(record['surface'] / 100000, 2)}</b> km²<br/>
        Couverture : <b>${roundFloat(record['pourcentage'], 2)}</b> %
        </div>`;
    },
  }),
  new Theme({
    ref: 'A4',
    name: 'ESOU – Informations générales profondeur',
    layers: ['profondeur', 'limite_nappe_trans_2009_2016'],
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
        Profondeur nappe / sol ${record['seuil']} m : <br/>
        Superficie : <b>${roundFloat(record['intersection'] / 1000000, 2)}</b> km²<br/>
        Couverture : <b>${roundFloat(record['pourcentage'], 2)}</b> %
        </div>`;
    },
  }),
  new Theme({
    ref: 'B1',
    name: 'Stations de mesures débits et stations alertes CRUE (DREAL Infogeo68)',
    layers: [
      'rivieres_de',
      'rivieres_ch',
      'rivieres',
      'rhin',
      'ObstEcoul',
      'stations_hydro',
      'stations_hydrique',
    ],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card" id="${record._id}">
      ${record.codehydro} ${record.nom || ''}<br/>
      ${record.nomcoursdeau}<br/>
      ${[
        `${record.url_hydro ? `<a target="_blank" href="${record.url_hydro}">Banque Hydro</a>` : ''}`,
        `${record.url_vigicrues ? `<a target="_blank" href="${record.url_vigicrues}">Vigicrues</a>` : ''}`,
        `${record.url_infogeo68 ? `<a target="_blank" href="${record.url_infogeo68}">Infogeo68</a>` : ''}`,
      ]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref]['debit'], 'codecommunes', record._id)}">${
        record.commune
      }</a> :
      ${[
        `${
          record.stations_dreal
            ? `
          <a href="${this.generateSearchUrl(
            themeUrlMapping[this.ref]['debit'],
            'codesbss',
            record.stations_dreal_codeshydro.join()
          )}">
          ${record.stations_dreal} stations DREAL</a>`
            : ''
        }`,
        `${
          record.stations_vigicrues
            ? `
          <a href="${this.generateSearchUrl(
            themeUrlMapping[this.ref]['crues'],
            'codesbss',
            record.stations_vigicrues_codeshydro.join()
          )}">
          ${record.stations_vigicrues} stations Vigicrues</a>`
            : ''
        }`,
        `${
          record.stations_infogeo68
            ? `
          <a href="${this.generateSearchUrl(
            themeUrlMapping[this.ref]['debit'],
            'codesbss',
            record.stations_infogeo68_codeshydro.join()
          )}">
          ${record.stations_infogeo68} stations Infogeo68</a>`
            : ''
        }`,
        `${record.coursdeau ? `${record.coursdeau} cours d'eau` : ''}`,
        `${record.obstacles ? `${record.obstacles} obstacles à l'écoulement` : ''}`,
      ]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'B2',
    layers: ['rivieres_de', 'rivieres_ch', 'rivieres', 'rhin', 'etat_ecologique', 'qualitometres_esu'],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card" id="${record._id}">
      ${record.codebss} ${record.nom || ''}<br/>
      ${[`${record.url_sierm ? `<a target="_blank" href="${record.url_sierm}">SIERM</a>` : ''}`]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codecommunes', record._id)}">
      ${record.commune} </a> :
      ${[
        `${
          record.stations_sierm
            ? `
          <a href="${this.generateSearchUrl(themeUrlMapping[this.ref], 'codesbss', record.codesbss.join())}">
          ${record.stations_sierm} stations SIERM</a>`
            : ''
        }`,
      ]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'B3',
    layers: ['rivieres_de', 'rivieres_ch', 'rivieres', 'rhin', 'zone_influence_riviere', 'Bassins_versants'],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card">
      ${record.type} ${record.codehydro}<br/>
      ${record.nom}<br/>
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      ${record.commune}:
      ${[`${record.bassins_versants ? `${record.bassins_versants} bassins versants` : ''}`]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'C1',
    layers: [
      'rivieres_de',
      'rivieres_ch',
      'rivieres',
      'rhin',
      'atlas_inondation_crue_rare_67',
      'atlas_inondation_remontee_nappe_68',
      'atlas_inondation_ruissellement_boue_68',
      'atlas_inondation_zone_inondable_68',
      'limite_nappe_trans_2009_2016',
    ],
    groupRenderer: function (record) {
      let records = [];
      for (let i = 0; i < record.rows.length; i++) {
        const row = record.rows[i];
        records.push(`${row.label} couvre <b>${roundFloat(row.surface / 100000, 2)}</b> km²<br/>
        soit <b>${roundFloat(row.pourcentage, 2)}</b> % de la surface de la commune.<br/>
        <u>Source</u> : ${row.document}`);
      }
      return `<div class="well well-sm group-card">
      <b>${humanize(record.id)}</b><br/>
      ${records.join('<br/><br/>')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'C2',
    layers: [
      'rivieres_de',
      'rivieres_ch',
      'rivieres',
      'rhin',
      'limite_nappe_trans_2009_2016',
      'step',
      'sites_industriels',
      'sites_pollues',
    ],
    featureRenderer: function (record) {
      return `<div class="well well-sm detail-card" id="${record._id}">
      ${record.type} ${record.numero} à ${record.commune}<br/>
      ${record.nom ? `${record.nom}<br/>` : ''}
      ${record.libelle ? `${record.libelle}<br/>` : ''}
      ${record.commentaires ? `${record.commentaires}<br/>` : ''}
      </div>`;
    },
    groupRenderer: function (record) {
      return `<div class="well well-sm group-card">
      ${record.commune}:
      ${[
        `${record.sites_industriels ? `${record.sites_industriels} sites industriels` : ''}`,
        `${record.sols_pollues ? `${record.sols_pollues} sites et sols pollués` : ''}`,
        `${record.etablissements_classes ? `${record.etablissements_classes} etablissements classés` : ''}`,
        `${record.steps ? `${record.steps} stations d'épuration` : ''}`,
      ]
        .filter((s) => s != '')
        .join(', ')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'C3',
    layers: [
      'directive_habitats_zsc',
      'directive_oiseaux_zps',
      'zones_humides_remarquables_interet_au_moins_regional',
      'reserves_naturelles_nationales',
      'reserves_naturelles_regionales',
    ],
    groupRenderer: function (record) {
      let rows = [];
      for (let i = 0; i < record.rows.length; i++) {
        let row = record.rows[i];

        let informations = [];
        if (row.numero) {
          informations.push(`Numéro : ${row.numero}`);
        }
        const date = row.date_protection || row.date_arret || row.date_decre;
        if (date) {
          informations.push(`Date : ${date}`);
        }
        if (row.statut) {
          informations.push(`<u>Status</u> : ${row.statut}`);
        }
        if (row.fiche_info) {
          informations.push(`<u>Fiche informations</u> :
            <a target="_blank" href="https://inpn.mnhn.fr/site/natura2000/${row.fiche_info}">Lien</a>`);
        }
        rows.push(
          `${row.nom || row.sitecode} couvre <b>${roundFloat(row.surface / 100000, 2)}</b> km²
          soit <b>${roundFloat(row.pourcentage, 2)}</b> % de la surface de la commune.
          ${informations.length > 0 ? `<br/>${informations.join('<br/>')}` : ''}`
        );
      }

      return `<div class="well well-sm group-card"><b>${humanize(record.id)}</b><br/>
      ${rows.join('<br/><br/>')}
      </div>`;
    },
  }),
  new Theme({
    ref: 'C4',
    layers: [
      'zones_agri_mieux_alsace',
      'zones_missions_eaux',
      // 'communes_nature',
      'sages_esu',
      'sages_esou',
      'aire_alimentation_captage',
    ],
    groupRenderer: function (record) {
      let rows = [];
      for (let i = 0; i < record.rows.length; i++) {
        const row = record.rows[i];
        rows.push(
          `${row.numero} ${row.nom}, ${row.description || ''}<br/>
          Couvre<b> ${roundFloat(row.intersection / 1000000, 2)}</b> km²
          soit <b>${roundFloat(row.pourcentage, 2)}</b> % de la surface de la commune.<br/>
          <u>Fiche informations</u> : <a target="_blank" href="${row.lien}">Lien</a>`
        );
      }
      return `<div class="well well-sm group-card">
      <b>${record.fichier}</b><br/>
      ${rows.join('<br/><br/>')}
      </div>`;
    },
  }),
];

export default themesDefinitions;
